import React from 'react';

import type { FormTextareaProps } from './forms.types';
import type { TextareaState } from '@/types/forms';

const FormTextarea = React.forwardRef<HTMLTextAreaElement, FormTextareaProps>(
  (
    {
      label,
      error,
      success,
      helpText,
      required = false,
      size = 'md',
      resize = 'vertical',
      className = '',
      disabled = false,
      id,
      rows = 4,
      ...props
    },
    ref
  ) => {
    const reactId = React.useId();
    const textareaId = id || `textarea-${reactId}`;

    // Determine state based on props
    const state: TextareaState = error
      ? 'error'
      : success
        ? 'success'
        : 'default';

    const sizeClasses = {
      sm: 'px-3 py-1.5 text-body-lg md:text-body-sm',
      md: 'px-4 py-3 text-body-lg md:text-body-sm leading-relaxed',
      lg: 'px-4 py-3.5 text-body-lg md:text-body-md leading-relaxed',
    };

    // Dark Sakura theme state classes
    const stateClasses = {
      default: 'border-stroke-default focus:border-primary-400/50',
      error: 'border-error-500 focus:border-error-500',
      success: 'border-success-500 focus:border-success-500',
    };

    const labelStateClasses = {
      default: 'text-neutral-300',
      error: 'text-error-400',
      success: 'text-success-400',
    };

    const resizeClasses = {
      none: 'resize-none',
      vertical: 'resize-y',
      horizontal: 'resize-x',
      both: 'resize',
    };

    const baseClasses =
      'appearance-none block w-full border rounded-lg bg-contrast-700 text-neutral-50 placeholder:text-neutral-500 focus:outline-none focus-visible:ring-state-focus-inset transition-colors';

    const disabledClasses = disabled
      ? 'bg-contrast-700 opacity-60 cursor-not-allowed'
      : '';

    const classes = [
      baseClasses,
      sizeClasses[size],
      stateClasses[state],
      resizeClasses[resize],
      disabledClasses,
      className,
    ]
      .filter(Boolean)
      .join(' ');

    // Determine message to display
    const message = error || success || helpText;
    const messageId = `${textareaId}-message`;

    return (
      <div className="space-y-1.5">
        {label && (
          <label
            htmlFor={textareaId}
            className={`block text-body-sm font-medium ${labelStateClasses[state]}`}
          >
            {label}
            {required && <span className="text-primary-400 ml-1">*</span>}
          </label>
        )}

        <div className="relative">
          <textarea
            ref={ref}
            id={textareaId}
            rows={rows}
            className={classes}
            disabled={disabled}
            required={required}
            aria-invalid={state === 'error'}
            aria-describedby={message ? messageId : undefined}
            {...props}
          />

          {/* State indicator icon positioned in top-right corner */}
          {(state === 'error' || state === 'success') && (
            <div className="pointer-events-none absolute top-2 right-3">
              {state === 'error' ? (
                <svg
                  className="h-5 w-5 text-error-400"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              ) : (
                <svg
                  className="h-5 w-5 text-success-400"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </div>
          )}
        </div>

        {message && (
          <p
            id={messageId}
            className={`text-label-xs ${
              error
                ? 'text-error-400'
                : success
                  ? 'text-success-400'
                  : 'text-neutral-500'
            }`}
          >
            {message}
          </p>
        )}
      </div>
    );
  }
);

FormTextarea.displayName = 'FormTextarea';

export default FormTextarea;
